import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DriverApi, DriverDTO } from './DriverApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Card, CardContent } from '@/components/ui/Card';

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

const DriverForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [tenantId] = useState<string>(() => user.tenantId || '');
  const [tenantName] = useState<string>(() => user.tenantName || '');

  const readOnly = mode === 'view';

  const [driver, setDriver] = useState<DriverDTO>({
    tenantId,
    tenantName,
    name: '',
    phone: '',
    licenseNo: '',
    licenseValidTill: '',
    isActive: true,
  });

  const [loading, setLoading] = useState<boolean>(!!id);

  useEffect(() => {
    if (!id || !tenantId) return;
    setLoading(true);
    DriverApi.get(tenantId, id)
      .then((res) => setDriver(res.data))
      .catch((e) => alert(e?.response?.data?.message || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, tenantId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setDriver((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!driver.tenantId) {
      alert('Tenant ID is required.');
      return;
    }
    if (!driver.name?.trim()) {
      alert('Name is required.');
      return;
    }
    if (!driver.licenseNo?.trim()) {
      alert('License number is required.');
      return;
    }
    if (!driver.licenseValidTill) {
      alert('License valid date is required.');
      return;
    }

    try {
      if (mode === 'create') {
        const res = await DriverApi.create(driver);
        alert('Driver created successfully!');
        navigate(`/driver/${res.data.id}`);
      } else if (mode === 'edit' && id) {
        await DriverApi.update(driver.tenantId, id, driver);
        alert('Driver updated successfully!');
        navigate(`/driver/${id}`);
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || e.message || 'Save failed');
    }
  };

  const infoList = useMemo(
    () =>
      Object.entries({
        tenantId: driver.tenantId,
        tenantName: driver.tenantName || '',
        status: driver.isActive ? 'Active' : 'Inactive',
      }),
    [driver]
  );

  const renderInput = (label: string, name: keyof DriverDTO, type = 'text', required = false) => (
    <div>
      <label className="block text-sm font-semibold mb-1">{label} {required && <span className="text-red-600">*</span>}</label>
      <input
        className="w-full border rounded px-3 py-2"
        type={type}
        name={name}
        value={(driver[name] as any) || ''}
        onChange={handleChange}
        disabled={readOnly}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {mode === 'create' ? 'Create Driver' : readOnly ? 'Driver Details' : 'Edit Driver'}
        </h1>
        <div className="flex gap-2">
          <button className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded px-3 py-2" onClick={() => navigate(-1)}>
            Back
          </button>
          {readOnly ? (
            <button className="bg-blue-600 hover:bg-blue-700 text-white rounded px-4 py-2" onClick={() => navigate(`/driver/${id}/edit`)} disabled={!id}>
              Edit
            </button>
          ) : (
            <button className="bg-blue-600 hover:bg-blue-700 text-white rounded px-4 py-2" onClick={handleSubmit}>
              {mode === 'create' ? 'Create' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Screen data area (like your GeneralTaskForm top panel) */}
      <Card>
        <CardContent className="flex items-start gap-3">
          <div className="text-blue-600 mt-1">
            <FontAwesomeIcon icon={faCircleInfo} />
          </div>
          <div>
            <h3 className="font-semibold">Driver Snapshot</h3>
            <ul className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              {infoList.map(([key, value]) => (
                <li key={key}>
                  <span className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>{' '}
                  <span className="text-gray-700">{String(value) || ''}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
         

        {renderInput('Name', 'name', 'text', true)}
        {renderInput('Phone', 'phone')}
        {renderInput('Alternate Number', 'alternateNumber')}
        
        {renderInput('License No', 'licenseNo', 'text', true)}
        {renderInput('License Valid Till', 'licenseValidTill', 'date', true)}

        <div className="md:col-span-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="isActive"
              checked={!!driver.isActive}
              onChange={handleChange}
              disabled={readOnly}
            />
            Active
          </label>
        </div>
      </form>
    </div>
  );
};

export default DriverForm;